/**
 * providerStore — providers/models cache over `GET /api/providers` +
 * `GET /api/models` (the server merges upstreams and caches 5m itself).
 * Client-side TTL mirrors that window so the Composer dropdown and the
 * Models tab read one shared, explicitly-invalidated cache.
 */
import { create } from 'zustand';
import {
  api,
  type CreateProviderBody,
  type ModelInfo,
  type ModelsMutationRes,
  type ModelsRefreshRes,
  type PatchProviderBody,
  type ProviderInfo,
  type ProviderTestRes,
} from '@/lib/api';

/** Client cache window — matches the server catalog TTL (5 minutes). */
export const PROVIDER_CACHE_TTL_MS = 5 * 60 * 1000;

/** Pure TTL check (exported for probes). */
export function isCacheFresh(fetchedAt: number | null, now: number): boolean {
  return fetchedAt !== null && now - fetchedAt < PROVIDER_CACHE_TTL_MS;
}

export type ProviderStore = {
  providers: ProviderInfo[];
  models: ModelInfo[];
  fetchedAt: number | null;
  loading: boolean;
  lastError: string | null;
  /** Latest live test result per provider id (from POST /:id/test). */
  testResults: Record<string, ProviderTestRes>;
  /** Provider id currently being tested (spinner), null when idle. */
  testingId: string | null;
  /** Refresh unless the cache is fresh (pass `force` to bypass the TTL). */
  refresh: (force?: boolean) => Promise<void>;
  /** Live fan-out refresh — `POST /api/models/refresh` hits every enabled
   * provider's `/v1/models` at once (REQ-032). The merged catalog is stored
   * directly; probed outcomes land in testResults so failed providers gain
   * their error badge without blocking the others (skipped = no key stays out). */
  refreshModelsLive: () => Promise<ModelsRefreshRes>;
  /** Drop the cache so the next read refetches (e.g. after provider CRUD). */
  invalidate: () => void;
  reset: () => void;
  /** Live connection test — stores the result under testResults[id]. On a
   * passing probe the shared providers/models cache is force-refreshed so
   * the row's model count and the Models tab update at once (REQ-031). */
  testProvider: (id: string) => Promise<ProviderTestRes>;
  /** Create a custom provider, then force-refresh the shared cache. */
  createProvider: (body: CreateProviderBody) => Promise<ProviderInfo>;
  /** Patch name/baseUrl/enabled/priority/apiKey, then force-refresh. */
  patchProvider: (id: string, body: PatchProviderBody) => Promise<ProviderInfo>;
  /** Delete a custom provider, then force-refresh. */
  deleteProvider: (id: string) => Promise<void>;
  /** Persist a new priority order, then force-refresh. */
  reorderProviders: (order: string[]) => Promise<void>;
  /** Enable/disable one model (optimistic, rolls back on failure). */
  setModelEnabled: (id: string, enabled: boolean) => Promise<void>;
  /** Bulk enable/disable (one PATCH; optimistic, rolls back on failure). */
  setModelsBulk: (flags: Record<string, boolean>) => Promise<ModelsMutationRes>;
};

const initial = {
  providers: [] as ProviderInfo[],
  models: [] as ModelInfo[],
  fetchedAt: null as number | null,
  loading: false,
  lastError: null as string | null,
  testResults: {} as Record<string, ProviderTestRes>,
  testingId: null as string | null,
};

export const useProviderStore = create<ProviderStore>()((set, get) => ({
  ...initial,

  refresh: async (force = false) => {
    if (!force && isCacheFresh(get().fetchedAt, Date.now())) return;
    set({ loading: true, lastError: null });
    try {
      const [providersRes, modelsRes] = await Promise.all([api.listProviders(), api.listModels()]);
      set({
        providers: providersRes.providers,
        models: modelsRes.models,
        fetchedAt: Date.now(),
        loading: false,
      });
    } catch (e) {
      set({ loading: false, lastError: e instanceof Error ? e.message : 'provider refresh failed' });
    }
  },

  invalidate: () => {
    set({ fetchedAt: null });
  },

  refreshModelsLive: async () => {
    set({ loading: true, lastError: null });
    try {
      const res = await api.refreshModels();
      set((s) => {
        const testResults = { ...s.testResults };
        for (const p of res.providers) {
          if (p.skipped) continue;
          testResults[p.id] = p.ok
            ? { ok: true, provider: p.id, modelCount: p.modelCount, latencyMs: p.latencyMs }
            : { ok: false, provider: p.id, error: p.error ?? 'refresh failed', latencyMs: p.latencyMs };
        }
        return { models: res.models, fetchedAt: Date.now(), loading: false, testResults };
      });
      return res;
    } catch (e) {
      set({ loading: false, lastError: e instanceof Error ? e.message : 'model refresh failed' });
      throw e;
    }
  },

  reset: () => {
    set({ ...initial });
  },

  testProvider: async (id: string) => {
    set({ testingId: id });
    try {
      const result = await api.testProvider(id);
      set((s) => ({ testingId: null, testResults: { ...s.testResults, [id]: result } }));
      // REQ-031: a passing probe means the merged catalog now carries this
      // provider's live models — force-refresh so the row's "N models" count
      // and the Models tab reflect the test result immediately instead of
      // sitting on the TTL-cached zero. Failures change nothing server-side.
      if (result.ok) await get().refresh(true);
      return result;
    } catch (e) {
      const fallback: ProviderTestRes = {
        ok: false,
        provider: id,
        error: e instanceof Error ? e.message : 'provider test failed',
      };
      set((s) => ({ testingId: null, testResults: { ...s.testResults, [id]: fallback } }));
      return fallback;
    }
  },

  createProvider: async (body: CreateProviderBody) => {
    const res = await api.createProvider(body);
    await get().refresh(true);
    return res.provider;
  },

  patchProvider: async (id: string, body: PatchProviderBody) => {
    const res = await api.patchProvider(id, body);
    await get().refresh(true);
    return res.provider;
  },

  deleteProvider: async (id: string) => {
    await api.deleteProvider(id);
    set((s) => {
      const testResults = { ...s.testResults };
      delete testResults[id];
      return { testResults };
    });
    await get().refresh(true);
  },

  reorderProviders: async (order: string[]) => {
    await api.reorderProviders(order);
    await get().refresh(true);
  },

  setModelEnabled: async (id: string, enabled: boolean) => {
    const previous = get().models;
    set({ models: previous.map((m) => (m.id === id ? { ...m, enabled } : m)) });
    try {
      const res = await api.setModelEnabled(id, enabled);
      set({ models: res.models, fetchedAt: Date.now() });
    } catch (e) {
      set({ models: previous, lastError: e instanceof Error ? e.message : 'model update failed' });
      throw e;
    }
  },

  setModelsBulk: async (flags: Record<string, boolean>) => {
    const previous = get().models;
    set({ models: previous.map((m) => (flags[m.id] === undefined ? m : { ...m, enabled: flags[m.id] as boolean })) });
    try {
      const res = await api.setModelsBulk(flags);
      set({ models: res.models, fetchedAt: Date.now() });
      return res;
    } catch (e) {
      set({ models: previous, lastError: e instanceof Error ? e.message : 'model update failed' });
      throw e;
    }
  },
}));
